<template>
<div class="content" id="company-list-content">
  <div class="header">
    <h1>{{ pageTitle }}</h1>
  </div>
  <div class="content-body table-wrapper">
    <div class="block">
      <Row :gutter="32" class="margin-bottom-20" data-test-id="company-filters">
        <Col span="7" data-test-id="company-name-filter">
          <Input search enter-button :placeholder="$t('companies.search')" @on-search="searchCompanies"/>
        </Col>
        <Col span="5" data-test-id="company-tag-filter">
          <CheckboxGroup :class="filtering ? 'active' : ''" v-model="filter">
            <Tooltip :content="filtering ? $t('companies.filter.off') : $t('companies.filter.on')" placement="top">
              <Button icon="ios-funnel" type="text" size="small" :class="filtering ? '' : 'gray'" @click="filtering = !filtering"></Button>
            </Tooltip>
            <div class="filters">
              <Checkbox label="Manufacturer" :disabled="!filtering"></Checkbox>
              <Checkbox label="Seller" :disabled="!filtering"></Checkbox>
              <Checkbox label="Client" :disabled="!filtering"></Checkbox>
            </div>
          </CheckboxGroup>
        </Col>
      </Row>
      <div class="text-align-right margin-bottom-20">
        <Button type="primary" @click="onAddCompany">{{ $t('companies.new') }}</Button>
      </div>
      <div data-test-id="company-list-table">
        <vxe-table
          border="inner"
          show-header-overflow
          show-overflow
          highlight-hover-row
          size="small"
          :row-class-name="rowClassName"
          :data="companies">
          <vxe-table-column field="name" :title="$t('companies.name')">
            <template v-slot="{ row }">
              <a @click="editCompany(row)">{{ row.name }}</a>
            </template>
          </vxe-table-column>
          <vxe-table-column field="aliases" :title="$t('companies.aliases')" width="80">
            <template v-slot="{ row }">
              <Badge :count="row.aliases ? row.aliases.length : 0" :type="(row.aliases ? row.aliases.length : 0) === 0 ? 'normal' : 'success'" show-zero></Badge>
            </template>
          </vxe-table-column>
          <vxe-table-column field="homepage_url" :title="$t('companies.website')">
            <template v-slot="{ row }">
              {{ row.homepage_url }} <a :href="row.homepage_url" v-if="row.homepage_url !== '' && row.homepage_url !== null" target="_blank" rel="noreferrer"><Icon type="md-open" /></a>
            </template>
          </vxe-table-column>
          <vxe-table-column field="tags" :title="$t('companies.tags')">
            <template v-slot="{ row }">
              <Tag v-for="item in row.tags" :key="item.id">{{ item }}</Tag>
            </template>
          </vxe-table-column>
          <vxe-table-column field="actions" :title="$t('companies.actions')" width="100">
            <template v-slot="{ row }">
              <Button icon="md-create" type="text" @click="editCompany(row)"></Button>
              <Poptip
                confirm
                transfer
                placement="top-end"
                :ok-text="$t('companies.confirm_button')"
                @on-ok="deleteCompany(row)">
                <span slot="title">
                  {{ $t('companies.confirm_tip') }}<strong>{{ row.name }}</strong>
                </span>
                <Button icon="md-trash" type="text"></Button>
              </Poptip>
            </template>
          </vxe-table-column>
        </vxe-table>
        <Spin size="large" fix v-if="loading"></Spin>
      </div>
      <div class="margin-top-10 text-align-right ">
        <Page
          show-elevator
          show-total
          show-sizer
          :total="total"
          :page-size="limit"
          :page-size-opts="[15, 20, 30, 50]"
          :current="currentPage"
          @on-change="onChangePage"
          @on-page-size-change="onChangePageSize">
        </Page>
      </div>
    </div>
    <Drawer
      v-model="editing"
      width="40%"
      :title="`${adding ? $t('companies.add_company') : $t('companies.edit_company')}${editingTarget.name}`"
      :mask-closable="!saving"
      :styles="styles">
      <Form ref="companyDataForm" :model="editingData">
        <FormItem :label="$t('companies.name')" label-position="top" prop="name" required>
          <Input
            type="text"
            v-model="editingData.name"
            :placeholder="$t('companies.name_placeholder')"
            :disabled="saving"
            @on-enter="adding ? handleAddCompany(editingData) : updateCompany(editingData)" />
        </FormItem>
        <div>
          <label class="ivu-form-item-label" id="aliases-label">
            <Badge :count="editingData.aliases ? editingData.aliases.length : 0" :type="(editingData.aliases ? editingData.aliases.length : 0) === 0 ? 'normal' : 'success'" show-zero>{{ $t('companies.aliases') }}</Badge>
          </label>
          <Input
            type="text"
            search
            :enter-button="$t('companies.add_alias')"
            v-model="tempAlias"
            :placeholder="$t('companies.aliases_placeholder')"
            :disabled="saving"
            @on-search="handleAddAlias(tempAlias)"
            @on-enter="handleAddAlias(tempAlias)" />
          </Input>
        </div>
        <div id="alias-list">
          <Tag
            closable
            size="large"
            v-if="editingData.aliases.length > 0"
            v-for="item in editingData.aliases"
            :key="item"
            :name="item"
            @on-close="handleRemoveAlias">
            {{ item }}
          </Tag>
        </div>
        <FormItem :label="$t('companies.website')" label-position="top">
          <Input
            type="url"
            v-model="editingData.homepage_url"
            :placeholder="$t('companies.website_placeholder')"
            :disabled="saving"
            @on-enter="adding ? handleAddCompany(editingData) : updateCompany(editingData)" />
          </Input>
        </FormItem>
        <label class="ivu-form-item-label" id="tags-label">{{ $t('companies.tags') }}</label>
        <FormItem>
          <CheckboxGroup v-model="editingData.tags" >
            <Checkbox label="Manufacturer" border :disabled="saving"></Checkbox>
            <Checkbox label="Seller" border :disabled="saving"></Checkbox>
            <Checkbox label="Client" border :disabled="saving"></Checkbox>
          </CheckboxGroup>
        </FormItem>
      </Form>
      <div class="company-edit-drawer-footer">
        <Button style="margin-right: 8px" @click="editing = false" :disabled="saving">{{ $t('form.cancel') }}</Button>
        <Button type="primary" @click="adding ? handleAddCompany(editingData) : updateCompany(editingData)" :loading="saving">{{ adding ? $t('form.ok') : $t('form.save') }}</Button>
      </div>
    </Drawer>
  </div>
</div>
</template>

<script>
import { mapActions } from 'vuex'
import Vue from 'vue'
import 'xe-utils'
import VXETable from 'vxe-table'
import 'vxe-table/lib/index.css'

Vue.use(VXETable)
Vue.prototype.$modal = VXETable.modal

export default {
  name: 'Companies',
  data () {
    return {
      value: '',
      total: 0,
      currentPage: 1,
      limit: 20,
      loading: true,
      saving: false,
      companies: [],
      editing: false,
      adding: false,
      changingPage: false,
      highlightId: -1,
      tempAlias: '',
      filtering: false,
      filter: [
        'Manufacturer',
        'Seller'
      ],
      editingTarget: {
        id: -1,
        name: '',
        homepage_url: '',
        tags: []
      },
      editingData: {
        id: -1,
        name: '',
        homepage_url: '',
        tags: []
      },
      styles: {
        height: 'calc(100% - 55px)',
        overflow: 'auto',
        paddingBottom: '53px',
        position: 'static'
      }
    }
  },
  computed: {
    pageTitle () {
      document.title = this.$t('menu.companies.list')
      return document.title
    }
  },
  mounted () {
    if (this.$route.query.filters) {
      this.filtering = true
      this.filter = [`${this.$route.query.filters}`]
    }
    this.loadCompanies()
  },
  methods: {
    ...mapActions([
      'getPagedCompanies',
      'addCompany',
      'updateSingleCompany',
      'deleteSingleCompany'
    ]),
    loadCompanies() {
      this.loading = true
      this.getPagedCompanies({
        page: this.currentPage,
        limit: this.limit,
        query: this.value,
        tags: this.filtering ? this.filter : null
      }).then(res => {
        this.companies = res.data
        this.total = res.meta.pagination.total
        this.currentPage = res.meta.pagination.current_page
        this.loading = false
        this.changingPage = false
      })
    },
    onChangePage (page) {
      if (!this.changingPage) {
        this.currentPage = page
        this.highlightId = -1
        this.loadCompanies()
      }
    },
    onChangePageSize (pageSize) {
      this.changingPage = true
      this.currentPage = Math.floor((this.currentPage - 1) * this.limit / pageSize) + 1
      this.limit = pageSize
      this.highlightId = -1
      this.loadCompanies()
    },
    onAddCompany () {
      this.editingTarget = {
        id: -1,
        name: '',
        homepage_url: '',
        tags: [],
        alias: null
      }
      this.editingData = {
        id: -1,
        name: '',
        homepage_url: '',
        tags: [],
        alias: null
      }
      this.adding = true
      this.editing = true
      this.highlightId = -1
    },
    handleAddCompany (company) {
      this.$refs['companyDataForm'].validate((valid) => {
        if (valid) {
          this.loading = true
          this.saving = true
          this.addCompany({
            name: company.name,
            homepage_url: company.homepage_url,
            tags: company.tags,
            aliases: _.uniq(company.aliases)
          }).then((res) => {
            this.editing = false
            this.highlightId = res.data.id
          }).finally(res => {
            this.saving = false
            this.value = ''
            this.getPagedCompanies({
              page: 1,
              limit: this.limit,
              query: this.value,
              tags: this.filtering ? this.filter : null
            }).then(res => {
              this.companies = res.data
              this.total = res.meta.pagination.total
              this.currentPage = res.meta.pagination.current_page
              this.loading = false
              this.changingPage = false
            })
          })
        }
      })
    },
    editCompany (comapny) {
      this.editingTarget = _.cloneDeep(comapny)
      this.editingData = _.cloneDeep(comapny)
      this.adding = false
      this.editing = true
      this.highlightId = -1
    },
    updateCompany (company) {
      this.loading = true
      this.saving = true
      this.updateSingleCompany({
        id: company.id,
        data: {
          name: company.name,
          homepage_url: company.homepage_url,
          tags: company.tags,
          aliases: _.uniq(company.aliases)
        }
      }).then((res) => {
        this.editing = false
        this.highlightId = res.data.id
      }).finally(res => {
        this.saving = false
        this.getPagedCompanies({
          page: this.currentPage,
          limit: this.limit,
          query: this.value,
          tags: this.filtering ? this.filter : null
        }).then(res => {
          this.companies = res.data
          this.total = res.meta.pagination.total
          this.currentPage = res.meta.pagination.current_page
          this.loading = false
          this.changingPage = false
        })
      })
    },
    deleteCompany (company) {
      this.loading = true
      this.deleteSingleCompany(company.id).finally(res => {
        this.getPagedCompanies({
          page: this.currentPage,
          limit: this.limit,
          query: this.value,
          tags: this.filtering ? this.filter : null
        }).then(res => {
          this.companies = res.data
          this.total = res.meta.pagination.total
          this.currentPage = res.meta.pagination.current_page
          this.loading = false
          this.changingPage = false
        })
      })
    },
    searchCompanies (keyword) {
      this.value = keyword
      this.getPagedCompanies({
        page: this.currentPage,
        limit: this.limit,
        query: keyword,
        tags: this.filtering ? this.filter : null
      }).then(res => {
        this.companies = res.data
        this.total = res.meta.pagination.total
        this.currentPage = res.meta.pagination.current_page
        this.loading = false
        this.changingPage = false
      })
    },
    rowClassName  ({ row, rowIndex }) {
      if (row.id === this.highlightId) {
        return 'row-highlight'
      }
    },
    handleAddAlias (alias) {
      if (typeof this.editingData.aliases === 'undefined' || this.editingData.aliases === null) {
        this.editingData.aliases = []
      }
      if (alias === '') {
        return
      }
      if (this.editingData.aliases.indexOf(alias) === -1) {
        this.editingData.aliases.push(alias)
        this.tempAlias = ''
      } else {
        this.$Message.warning(this.$t('companies.has_alias'))
      }
    },
    handleRemoveAlias (event, name) {
      if (typeof this.editingData.aliases === 'undefined' || this.editingData.aliases === null) {
        this.editingData.aliases = []
      }
      const index = this.editingData.aliases.indexOf(name)
      if (index !== -1) {
        this.editingData.aliases.splice(index, 1)
      }
    }
  }
}
</script>

<style lang="less">
@keyframes highlight {
  0% {
    background-color: rgb(255, 245, 188);
  }
  100% {
    background-color: #fff;
  }
}
div.vxe-table {
  table.vxe-table--header thead {
    background-color: #F8F8F9;
  }
  td.vxe-body--column button:focus {
    box-shadow: none!important;
  }
  .vxe-body--row.row-highlight {
    animation: highlight 3s;
  }
}
.company-edit-drawer-footer {
  width: 100%;
  position: absolute;
  bottom: 0;
  left: 0;
  border-top: 1px solid #e8e8e8;
  padding: 10px 16px;
  text-align: right;
  background: #fff;
}
#tags-label, #aliases-label {
  width: 100%;
  text-align: left;
  .ivu-badge-count {
    top: -3px;
    right: -22px;
  }
}
#tags-label ~ div.ivu-form-item label.ivu-checkbox-group-item {
  font-weight: normal;
  user-select: none;
}
.ivu-poptip-inner .ivu-poptip-footer button.ivu-btn-primary {
  background-color: #ed4014;
  border-color: #ed4014;
  &:focus {
    box-shadow: 0 0 0 2px fade(#ed4014, 20%);
  }
}
#add-alias {
  margin-left: 5px;
}
#alias-list {
  width: 100%;
  margin-top: 10px;
  margin-bottom: 24px;
}
#company-list-content {
  overflow: visible;
  .ivu-spin-fix {
    z-index: 100;
  }
  div.ivu-col.ivu-col-span-5 {
    padding: 0!important;
    width: auto;
    .ivu-checkbox-group {
      padding: 4.5px 7px;
      border: 1px solid #dcdee2;
      border-radius: 4px;
      user-select: none;
      &.active {
        border: 1px solid #2D8CF0;
      }
      button {
        margin-right: 10px;
        width: 21px;
        height: 21px;
        vertical-align: middle;
        &.gray {
          color: #8f8f8f;
        }
      }
      .filters {
        display: inline-block;
        height: 21px;
        vertical-align: middle;
      }
    }
  }
}
</style>
